import React from 'react';

import AccessRow from './AccessRow';
import { grantedModules, hasReportAccess, scopedMenuNote } from './homeAccess';

import Button from 'CommonComponents/Button';

/**
 * What a user with no allocations sees.
 *
 * Leads with what they CAN do. The alternative — rendering the normal page with
 * every section empty — is the exact failure this Home replaces: a wall of
 * empty tables reads as "the product is broken", not as "nothing is assigned to
 * you yet".
 *
 * That is also why this carries its own access summary instead of the full
 * "Your access" panel. This user is, by the definition that selected this
 * component, entitled to no department, venue or revenue department — so those
 * three rows could only ever read "None", and three rows of None are the empty
 * shell in miniature. Modules are the one axis they do hold something on, so
 * modules are the one axis shown, with the same department-scoped note as the
 * full panel.
 *
 * The reports call to action disappears entirely for a user with no report
 * grant, rather than being shown disabled. A link to a Report Centre they
 * cannot open would be a worse landing than no link.
 */
export default function HomeEmptyState({ eventName, access = {}, reportsUrl }) {
    const modules = access.modules;
    const showReports = !!reportsUrl && hasReportAccess(modules);
    const grants = grantedModules(modules);
    const modulesNote = scopedMenuNote(access);

    return (
        <div className="tw-rounded-lg tw-bg-white tw-px-6 tw-py-12 tw-shadow-primary">
            <div className="tw-text-center">
                <h3 className="tw-text-lg tw-font-semibold tw-text-charcoal">{$t("Nothing's assigned to you yet")}</h3>
                <p className="tw-mx-auto tw-mt-2 tw-max-w-md tw-text-0.5sm tw-text-charcoal-60">
                    {$t(
                        'You have read access to {{event}}. When you are added to a department, venue, approval workflow or planning activity, it will show up here as your personal home.',
                        { event: eventName },
                    )}
                </p>
            </div>

            {/* No emptyLabel: the row renders only when there is something to
                list, so "None" is unreachable here. A user with no modules
                either gets the welcome copy and the reports link alone. */}
            {grants.length > 0 && (
                <div className="tw-mx-auto tw-mt-8 tw-max-w-md">
                    <AccessRow label={$t('Modules')} items={grants} note={modulesNote} />
                </div>
            )}

            {showReports && (
                <div className="tw-mt-8 tw-text-center">
                    <p className="tw-mx-auto tw-max-w-md tw-text-0.5sm tw-text-charcoal-60">
                        {$t('In the meantime you can browse the reports you have access to.')}
                    </p>
                    <div className="tw-mt-4">
                        <Button as="a" variant="primary" href={reportsUrl}>
                            {$t('Open Report Centre')}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
